import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import axios from 'axios';
import { API_URL, getWsBaseUrl } from '../config';
import { useProject } from '../context/ProjectContext';
import TicketDetailPanel from './TicketDetailPanel';
import { ToastContainer, ToastType } from './Toast';
import AnalyticsCard from './AnalyticsCard';
import NotificationsCard from './NotificationsCard';
import PredictionCard from './PredictionCard';
import { trackContext } from '../utils/contextTracking';
import { useWakeWord } from '../hooks/useWakeWord';
import { useVoiceInterruption } from '../hooks/useVoiceInterruption';
import ComplicConversationSidebar, { ConversationSummary } from './ComplicConversationSidebar';
import { notifyJiraError } from '../utils/jiraErrors';
import {
  AgentCommandBlock,
  AgentCommandCard,
  applyCommandEvent,
  commandsFromAgentLogs,
} from './AgentCommandFeed';


interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  kind?: 'text' | 'command' | 'activity';
  command?: AgentCommandBlock;
  toolName?: string | null;
  ticketKeys?: string[];  // Extracted ticket keys from message
  jobs?: JobInfo[];  // Extracted job info from message
  analyticsData?: any;  // Analytics data to display
  notificationsData?: any;  // Notifications data to display
  predictionData?: any;  // Prediction data to display
}

interface JobInfo {
  job_id: string;
  action_type: string;
  status: string;
  progress?: number;
}

interface TicketInfo {
  key: string;
  summary: string;
  status: string;
  assignee?: string | null;
  priority?: string | null;
  labels?: string[];
  sprint?: string | null;
}

interface ComplicChatProps {
  onClose: () => void;
  onOpenSettings?: () => void;
  onWakeWordActivated?: () => void;
  onWakeWordDetected?: (detected: boolean) => void;
  onVoiceTimeout?: () => void;
  onOpenTicket?: (ticketKey: string, section?: string | null) => void;
}

export interface ComplicChatRef {
  startVoice: () => void;
  stopVoice: () => void;
  getVoiceState: () => { isListening: boolean; isSpeaking: boolean };
  openTicketConversation: (ticketKey: string, title?: string) => Promise<void>;
  openConversation: (id: number) => Promise<void>;
}

const ComplicChat = forwardRef<ComplicChatRef, ComplicChatProps>(({ onClose, onOpenSettings, onWakeWordActivated, onWakeWordDetected, onVoiceTimeout, onOpenTicket }, ref) => {
  const { currentProject } = useProject();

  // Split view state
  const [showSplitView, setShowSplitView] = useState(false);
  const [selectedTicketForDetail, setSelectedTicketForDetail] = useState<TicketInfo | null>(null);

  // Toast notifications
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type: ToastType }>>([]);

  // Persistent conversations
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
  const [activeConversation, setActiveConversation] = useState<ConversationSummary | null>(null);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [showConversationSidebar, setShowConversationSidebar] = useState(true);
  const [sessionLiveLog, setSessionLiveLog] = useState<string | null>(null);
  const [thinkingDetail, setThinkingDetail] = useState<string | null>(null);
  const [agentCommands, setAgentCommands] = useState<AgentCommandBlock[]>([]);
  const [commandExpanded, setCommandExpanded] = useState<Record<string, boolean>>({});
  const [liveCommand, setLiveCommand] = useState<string | null>(null);
  const [startingWork, setStartingWork] = useState(false);
  const [stopping, setStopping] = useState(false);

  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: 'Hello! I\'m Complic, your AI coding assistant. How can I help you today?',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastSentText, setLastSentText] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [selectedAssistant, setSelectedAssistant] = useState<string>('default');
  const [modelUsed, setModelUsed] = useState<string>('');
  const [assistantUsed, setAssistantUsed] = useState<string>('');
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [language, setLanguage] = useState<'en-US' | 'cs-CZ'>('cs-CZ');
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [, setCurrentVoice] = useState<string>('');
  const [selectedCloudVoice, setSelectedCloudVoice] = useState<string>('cs-CZ-AntoninNeural');
  const [availableCloudVoices, setAvailableCloudVoices] = useState<any[]>([]);

  // Wake word state
  const [wakeWordEnabled, setWakeWordEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('complic_wake_word_enabled');
    return saved !== null ? saved === 'true' : false; // Default to disabled
  });
  const [wakeWordDetected, setWakeWordDetected] = useState(false);

  // Notify parent component about wake word detection state
  useEffect(() => {
    if (onWakeWordDetected) {
      onWakeWordDetected(wakeWordDetected);
    }
  }, [wakeWordDetected, onWakeWordDetected]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const activeConversationIdRef = useRef<number | null>(null);
  const chatAbortRef = useRef<AbortController | null>(null);
  const recognitionRef = useRef<any>(null);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const shouldStopSpeakingRef = useRef<boolean>(false);
  const shouldContinueListeningRef = useRef<boolean>(false);
  const aiStartedSpeakingAtRef = useRef<number>(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const isSpeakingRef = useRef<boolean>(false);
  const restartMicTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wakeWordWasEnabledRef = useRef<boolean>(false);
  const wakeWordRestartTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wakeWordControlRef = useRef<any>(null);
  const wakeWordStartedRef = useRef<boolean>(false);

  // Helper to update isSpeaking state AND ref
  const setIsSpeakingWithRef = (value: boolean) => {
    isSpeakingRef.current = value;
    setIsSpeaking(value);
  };

  const mapApiMessages = (apiMessages: any[]): Message[] =>
    (apiMessages || []).map((m: any) => {
      const toolName = m.tool_name || null;
      const isActivity =
        toolName === 'agent_activity' ||
        toolName === 'agent_milestone' ||
        toolName === 'agent_chat_ack' ||
        toolName === 'agent_user_note';
      return {
        id: String(m.id),
        role: m.role === 'user' ? 'user' : 'assistant',
        content: m.content || '',
        timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
        kind: isActivity ? 'activity' : 'text',
        toolName,
        ticketKeys: m.role !== 'user' ? extractTicketKeys(m.content || '') : undefined,
      };
    });

  const syncCommandsIntoMessages = useCallback(
    (msgs: Message[], commands: AgentCommandBlock[]): Message[] => {
      // Canonical list: keep non-command messages, replace ALL command bubbles
      // from the live command state (avoids hist-* vs cmd-* ID duplicates).
      const nonCmd = msgs.filter((m) => m.kind !== 'command');
      const cmdMsgs: Message[] = commands.map((c) => ({
        id: `command-${c.id}`,
        role: 'assistant' as const,
        kind: 'command' as const,
        content: c.command,
        command: c,
        timestamp: new Date(),
      }));
      // Keep relative order: non-command stream first, then current command cards
      // (commands are live agent work — appear after chat/activity in the panel).
      // If there were command cards interleaved, re-append at end of their cluster:
      // find first command index in original, splice there.
      const firstCmdIdx = msgs.findIndex((m) => m.kind === 'command');
      if (firstCmdIdx < 0) {
        return [...nonCmd, ...cmdMsgs];
      }
      const before = msgs.slice(0, firstCmdIdx).filter((m) => m.kind !== 'command');
      const after = msgs.slice(firstCmdIdx).filter((m) => m.kind !== 'command');
      return [...before, ...cmdMsgs, ...after];
    },
    []
  );

  const refreshConversations = useCallback(async () => {
    try {
      setConversationsLoading(true);
      const response = await axios.get(`${API_URL}/api/complic/conversations`, {
        params: { project_id: currentProject?.id }
      });
      setConversations(response.data.conversations || []);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setConversationsLoading(false);
    }
  }, [currentProject?.id]);

  const selectConversation = useCallback(async (id: number, opts?: { keepLive?: boolean }) => {
    try {
      const response = await axios.get(`${API_URL}/api/complic/conversations/${id}`);
      const conv = response.data.conversation;
      const sameSession = activeConversationIdRef.current === conv.id;
      setActiveConversationId(conv.id);
      activeConversationIdRef.current = conv.id;
      setActiveConversation(conv);
      if (!opts?.keepLive && !sameSession) {
        setSessionLiveLog(null);
        setAgentCommands([]);
        setCommandExpanded({});
        setLiveCommand(null);
        setMessages(mapApiMessages(response.data.messages));
      } else {
        if (conv.status_detail) setSessionLiveLog(conv.status_detail);
        // Soft refresh: text/activity only — never touch command bubbles
        setMessages((prev) => {
          const cmds = prev.filter((m) => m.kind === 'command');
          const fromApi = mapApiMessages(response.data.messages).filter(
            (m) => m.kind !== 'command'
          );
          return [...fromApi, ...cmds];
        });
      }
      // Hydrate command cards ONLY on first open of a conversation, and ONLY
      // from an active run — never dump cancelled history after a chat message.
      if (conv.ticket_key && !opts?.keepLive && !sameSession) {
        try {
          const runsRes = await axios.get(`${API_URL}/api/agents/runs`, {
            params: { ticket_key: conv.ticket_key, limit: 5 },
          });
          const runs = runsRes.data.runs || [];
          const live = runs.find((r: any) =>
            ['running', 'waiting_user', 'pending', 'queued'].includes(r.status)
          );
          if (live?.logs?.length) {
            const blocks = commandsFromAgentLogs(live.logs);
            if (blocks.length) {
              setAgentCommands(blocks);
              const expanded: Record<string, boolean> = {};
              blocks.forEach((b) => {
                expanded[b.id] = b.status === 'running';
              });
              setCommandExpanded(expanded);
              setLiveCommand(blocks.find((b) => b.status === 'running')?.command || null);
              setMessages((prev) => syncCommandsIntoMessages(prev, blocks));
            }
          }
        } catch {
          /* ignore */
        }
      }
      if (conv.assistant) setAssistantUsed(conv.assistant);
      if (conv.model) setModelUsed(conv.model);
    } catch (err) {
      console.error('Failed to open conversation:', err);
      addToast('Nepodařilo se otevřít konverzaci', 'error');
    }
  }, []);

  // Live updates for ticket session work
  useEffect(() => {
    if (!activeConversationId) return;
    const wsUrl = `${getWsBaseUrl()}/ws/conv-${activeConversationId}`;
    const ws = new WebSocket(wsUrl);
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'session_status') {
          if (data.status_detail) {
            setThinkingDetail(data.status_detail);
            setSessionLiveLog(data.status_detail);
          }
          setActiveConversation((prev) =>
            prev && prev.id === data.conversation_id
              ? {
                  ...prev,
                  status: data.status || prev.status,
                  status_detail: data.status_detail ?? prev.status_detail,
                  progress: data.progress ?? prev.progress,
                  branch_name: data.branch_name ?? prev.branch_name,
                }
              : prev
          );
          setConversations((prev) =>
            prev.map((c) =>
              c.id === data.conversation_id
                ? {
                    ...c,
                    status: data.status || c.status,
                    status_detail: data.status_detail ?? c.status_detail,
                    progress: data.progress ?? c.progress,
                    branch_name: data.branch_name ?? c.branch_name,
                  }
                : c
            )
          );
        } else if (data.type === 'session_message' && data.message) {
          const mapped = mapApiMessages([data.message])[0];
          setMessages((prev) => {
            if (prev.some((m) => m.id === mapped.id)) return prev;
            return [...prev, mapped];
          });
          refreshConversations();
        } else if (data.type === 'session_log') {
          const msg = data.data?.message || data.data?.data?.message;
          const et = data.data?.event_type;
          const state = data.data?.state;
          const payload = data.data?.payload || {};
          // Commands are handled via agent_event only — session_log would duplicate cards
          if (et === 'command') {
            if (state === 'start') {
              const cmd = payload.command || msg;
              setLiveCommand(cmd);
              setSessionLiveLog(`▶ ${cmd}`);
            } else if (state === 'running' && (payload.line || msg)) {
              setSessionLiveLog(payload.line || msg);
            } else if (state === 'end') {
              setLiveCommand(null);
            }
          } else if (msg) {
            setSessionLiveLog(String(msg));
            setThinkingDetail(String(msg));
          }
        } else if (data.type === 'agent_event' && data.event) {
          const ev = data.event;
          if (ev.type === 'command') {
            setAgentCommands((prev) => {
              const next = applyCommandEvent(prev, ev);
              setMessages((msgs) => syncCommandsIntoMessages(msgs, next));
              return next;
            });
            if (ev.state === 'start') {
              const cmd = ev.payload?.command || ev.message;
              setLiveCommand(cmd);
              setSessionLiveLog(`▶ ${cmd}`);
            } else if (ev.state === 'running' && (ev.payload?.line || ev.message)) {
              setSessionLiveLog(ev.payload?.line || ev.message);
            } else if (ev.state === 'end') {
              setLiveCommand(null);
            }
          } else if (ev.message) {
            setSessionLiveLog(String(ev.message));
          }
          if (ev.type === 'message' || ev.type === 'approval') {
            selectConversation(activeConversationId, { keepLive: true });
          }
        } else if (data.type === 'agent_status') {
          refreshConversations();
          // Keep command bubbles in chat after finish — only clear live spinner
          if (['success', 'failed', 'cancelled'].includes(data.status)) {
            setLiveCommand(null);
          }
          if (activeConversationId) {
            setActiveConversation((prev) =>
              prev
                ? {
                    ...prev,
                    status:
                      data.status === 'waiting_user'
                        ? 'waiting_user'
                        : data.status === 'success'
                          ? 'done'
                          : data.status === 'failed'
                            ? 'failed'
                            : data.status === 'cancelled'
                              ? 'idle'
                              : 'running',
                    status_detail:
                      data.status_detail || data.summary || data.error || prev.status_detail,
                    progress: data.progress ?? prev.progress,
                  }
                : prev
            );
          }
        }
      } catch (e) {
        console.error('session ws parse error', e);
      }
    };
    ws.onerror = () => {
      /* ignore — poll via status on next open */
    };
    const ping = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send('ping');
    }, 25000);
    return () => {
      clearInterval(ping);
      ws.close();
    };
  }, [activeConversationId, refreshConversations, selectConversation]);

  // User-level progress (works even before conv WS is ready / during first message)
  useEffect(() => {
    let ws: WebSocket | null = null;
    let cancelled = false;
    (async () => {
      try {
        const me = await axios.get(`${API_URL}/api/auth/me`);
        if (cancelled || !me.data?.id) return;
        ws = new WebSocket(`${getWsBaseUrl()}/ws/complic-${me.data.id}`);
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (
              (data.type === 'chat_progress' || data.type === 'session_status') &&
              data.status_detail
            ) {
              setThinkingDetail(data.status_detail);
              setSessionLiveLog(data.status_detail);
            }
            // Relay iframe_command if BrowserPanel isn't the only listener
            if (data.type === 'iframe_command' && data.id) {
              window.dispatchEvent(new CustomEvent('complic-iframe-command', { detail: data }));
            }
          } catch {
            /* ignore */
          }
        };
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
      ws?.close();
    };
  }, []);

  const startTicketWork = useCallback(
    async (mode: 'plan' | 'implement' | 'full' | 'push') => {
      if (!activeConversationId || !currentProject?.id) {
        addToast('Nejdřív otevři ticket session a vyber projekt', 'error');
        return;
      }
      try {
        setStartingWork(true);
        const response = await axios.post(
          `${API_URL}/api/complic/conversations/${activeConversationId}/work`,
          {
            mode,
            project_id: currentProject.id,
            reason: `User started Developer ${mode} from Complic UI`,
          }
        );
        addToast(
          response.data.queued
            ? `Developer ${mode} ve frontě (pozice ${response.data.position}) · ${(response.data.run_id || '').slice(0, 8)}`
            : `Developer ${mode} · run ${(response.data.run_id || '').slice(0, 8)}…`,
          'success'
        );
        setActiveConversation((prev) =>
          prev
            ? {
                ...prev,
                status: 'running',
                status_detail: response.data.queued
                  ? `Ve frontě (pozice ${response.data.position})`
                  : `Developer ${mode}…`,
              }
            : prev
        );
      } catch (err: any) {
        addToast(err.response?.data?.detail || 'Nepodařilo se spustit agenta', 'error');
      } finally {
        setStartingWork(false);
      }
    },
    [activeConversationId, currentProject?.id]
  );

  const isAgentBusy = ['running', 'pending', 'queued', 'waiting_user'].includes(
    activeConversation?.status || ''
  );
  const isBusy = loading || isAgentBusy;

  const handleStop = useCallback(async () => {
    if (stopping) return;
    setStopping(true);
    try {
      // Abort in-flight chat HTTP request (Thinking…)
      if (chatAbortRef.current) {
        chatAbortRef.current.abort();
        chatAbortRef.current = null;
      }

      const convId = activeConversationIdRef.current;
      if (convId && isAgentBusy) {
        await axios.post(`${API_URL}/api/complic/conversations/${convId}/stop`);
        setActiveConversation((prev) =>
          prev
            ? { ...prev, status: 'idle', status_detail: 'Zastaveno' }
            : prev
        );
        await selectConversation(convId, { keepLive: true });
        refreshConversations();
      }

      setLoading(false);
      setThinkingDetail(null);
      setSessionLiveLog(null);
      setLiveCommand(null);
      setAgentCommands((prev) =>
        prev.map((c) =>
          c.status === 'running'
            ? {
                ...c,
                status: 'error' as const,
                returncode: 130,
                output: [...c.output, '[complic] stopped by user'],
              }
            : c
        )
      );
      setMessages((prev) =>
        prev.map((m) =>
          m.kind === 'command' && m.command?.status === 'running'
            ? {
                ...m,
                command: {
                  ...m.command,
                  status: 'error' as const,
                  returncode: 130,
                  output: [...m.command.output, '[complic] stopped by user'],
                },
              }
            : m
        )
      );
      addToast('Zastaveno — můžeš spustit znovu', 'success');
    } catch (err: any) {
      // Still clear local busy state so UI unlocks
      setLoading(false);
      setThinkingDetail(null);
      addToast(err?.response?.data?.detail || 'Stop selhal, zkus znovu', 'error');
    } finally {
      setStopping(false);
    }
  }, [stopping, isAgentBusy, selectConversation, refreshConversations]);

  const createGeneralConversation = useCallback(async () => {
    try {
      const response = await axios.post(`${API_URL}/api/complic/conversations`, {
        kind: 'general',
        project_id: currentProject?.id
      });
      await refreshConversations();
      await selectConversation(response.data.id);
    } catch (err) {
      console.error('Failed to create conversation:', err);
      addToast('Nepodařilo se vytvořit chat', 'error');
    }
  }, [currentProject?.id, refreshConversations, selectConversation]);

  const openTicketConversation = useCallback(async (ticketKey: string, title?: string) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/complic/conversations/ticket/${encodeURIComponent(ticketKey)}`,
        null,
        { params: { project_id: currentProject?.id, title } }
      );
      await refreshConversations();
      const conv = response.data.conversation;
      setActiveConversationId(conv.id);
      activeConversationIdRef.current = conv.id;
      setActiveConversation(conv);
      setMessages(mapApiMessages(response.data.messages));
    } catch (err) {
      console.error('Failed to open ticket conversation:', err);
      addToast(`Nepodařilo se otevřít ${ticketKey}`, 'error');
    }
  }, [currentProject?.id, refreshConversations]);

  // Helper to send message programmatically
  const sendMessageProgrammatically = async (text: string) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setLastSentText(text);
    setLoading(true);
    setThinkingDetail('Thinking…');

    const abort = new AbortController();
    chatAbortRef.current = abort;

    try {
      const response = await axios.post(
        `${API_URL}/api/complic/chat`,
        {
          message: text,
          model: selectedModel,
          language: language,
          history: messages.map(m => ({ role: m.role, content: m.content })),
          project_id: currentProject?.id,
          conversation_id: activeConversationIdRef.current,
          ticket_key: activeConversation?.ticket_key || undefined,
        },
        { signal: abort.signal }
      );
      setModelUsed(response.data.model_used || '');
      setAssistantUsed(response.data.assistant_used || selectedAssistant);

      if (response.data.conversation_id && !activeConversationIdRef.current) {
        activeConversationIdRef.current = response.data.conversation_id;
        setActiveConversationId(response.data.conversation_id);
        await refreshConversations();
      }

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date(),
        ticketKeys: extractTicketKeys(response.data.response)
      };

      setMessages(prev => [...prev, assistantMessage]);
      refreshConversations();

      if (response.data.action) {
        handleAction(response.data.action);
      }

      // Speak only in active voice mode (not typed chat)
      if (shouldContinueListeningRef.current) {
        await speakText(response.data.response);
      }
    } catch (err: any) {
      if (axios.isCancel(err) || err?.code === 'ERR_CANCELED' || abort.signal.aborted) {
        return;
      }
      console.error('Chat error:', err);
      const detail = err?.response?.data?.detail;
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: detail || 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      if (chatAbortRef.current === abort) {
        chatAbortRef.current = null;
      }
      setLoading(false);
      setThinkingDetail(null);
    }
  };

  // Wake word detection hook - SIMPLE: just activate mic, nothing else!
  const handleWakeWordDetected = (remainingText?: string) => {
    console.log('🎯 Wake word detected');

    // IMMEDIATELY stop wake word listening
    if (wakeWordControlRef.current) {
      wakeWordControlRef.current.stop();
    }

    // Show visual feedback
    setWakeWordDetected(true);

    // ALWAYS activate voice assistant (simulate mic button click)
    if (onWakeWordActivated) {
      onWakeWordActivated();
    }

    // Hide visual feedback after 2s
    setTimeout(() => {
      setWakeWordDetected(false);
    }, 2000);
  };

  const wakeWordControl = useWakeWord({
    enabled: false, // Manually controlled, not auto-enabled
    onWakeWordDetected: handleWakeWordDetected,
    onError: (error) => {
      console.error('Wake word error:', error);
      addToast('Wake word not supported in this browser', 'error');
    }
  });

  // Store wake word control in ref so it's available in callbacks
  useEffect(() => {
    wakeWordControlRef.current = wakeWordControl;
  }, [wakeWordControl]);

  // Start wake word if enabled on mount (only once)
  useEffect(() => {
    if (wakeWordEnabled && wakeWordControl && wakeWordControl.isInitialized && !wakeWordStartedRef.current) {
      wakeWordControl.start();
      wakeWordStartedRef.current = true;
    }
  }, [wakeWordEnabled, wakeWordControl]);

  // Voice interruption hook
  useVoiceInterruption({
    enabled: true,
    isSpeaking,
    audioContext: audioContextRef.current,
    analyser: analyserRef.current,
    onInterruption: () => {
      console.log('🛑 User interrupted AI speech');
      stopSpeaking();
    },
    threshold: 50, // Adjust based on testing
    checkInterval: 100
  });

  // Expose voice control methods to parent via ref
  useImperativeHandle(ref, () => ({
    startVoice: async () => {
      // Initialize microphone if not done yet
      if (!micStreamRef.current) {
        await initMicrophoneWithEchoCancellation();
      }

      if (recognitionRef.current && !isListening) {
        try {
          shouldContinueListeningRef.current = true;
          recognitionRef.current.start();
          setIsListening(true);
          console.log('🎤 Started continuous voice mode');
        } catch (e) {
          console.error('Failed to start recognition:', e);
        }
      }
    },
    stopVoice: () => {
      shouldStopSpeakingRef.current = true;
      shouldContinueListeningRef.current = false;

      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }

      // Restart wake word after 2 seconds if voice assistant is fully stopped
      if (wakeWordRestartTimeoutRef.current) {
        clearTimeout(wakeWordRestartTimeoutRef.current);
      }
      wakeWordRestartTimeoutRef.current = setTimeout(() => {
        if (wakeWordEnabled && !isListening && !isSpeaking && wakeWordControlRef.current) {
          wakeWordControlRef.current.start();
        }
      }, 2000);

      window.speechSynthesis.cancel();
      setIsListening(false);
      setIsSpeakingWithRef(false);
    },
    getVoiceState: () => ({
      isListening,
      isSpeaking
    }),
    openTicketConversation: async (ticketKey: string, title?: string) => {
      await openTicketConversation(ticketKey, title);
    },
    openConversation: async (id: number) => {
      await selectConversation(id);
    }
  }));

  // Toast helper functions
  const addToast = (message: string, type: ToastType) => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+K - Focus chat input
      if (e.ctrlKey && e.key === 'k') {
        e.preventDefault();
        document.querySelector<HTMLTextAreaElement>('textarea[placeholder*="Type your message"]')?.focus();
      }

      // Ctrl+M - Toggle microphone
      if (e.ctrlKey && e.key === 'm') {
        e.preventDefault();
        toggleListening();
      }

      // Ctrl+L - Clear chat history
      if (e.ctrlKey && e.key === 'l') {
        e.preventDefault();
        clearChatHistory();
      }

      // Ctrl+Shift+S - Toggle split view
      if (e.ctrlKey && e.shiftKey && e.key === 'S') {
        e.preventDefault();
        setShowSplitView(prev => !prev);
      }

      // Esc - Stop busy work first, else stop speaking, else close
      if (e.key === 'Escape') {
        e.preventDefault();
        if (loading || isAgentBusy) {
          handleStop();
        } else if (isSpeaking) {
          stopSpeaking();
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSpeaking, loading, isAgentBusy, handleStop]);

  // Listen for wake word setting changes
  useEffect(() => {
    const handleStorageChange = () => {
      const newValue = localStorage.getItem('complic_wake_word_enabled') === 'true';
      setWakeWordEnabled(newValue);
      console.log('🔄 Wake word setting changed:', newValue);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    // Load saved voice from localStorage
    const savedVoice = localStorage.getItem('complic_selected_voice');
    if (savedVoice) {
      setSelectedCloudVoice(savedVoice);
      console.log('📢 Loaded voice from localStorage:', savedVoice);
    }

    fetchDefaultModel();
    fetchAvailableVoices();
    initMicrophoneWithEchoCancellation();
    initSpeechRecognition();

    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices();
      setVoices(availableVoices);
      console.log('Available voices:', availableVoices.map(v => `${v.name} (${v.lang})`));
    };
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;

    const bootConversations = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/complic/conversations`, {
          params: { project_id: currentProject?.id }
        });
        const list: ConversationSummary[] = response.data.conversations || [];
        setConversations(list);
        setConversationsLoading(false);
        if (list.length > 0) {
          await selectConversation(list[0].id);
        } else {
          await createGeneralConversation();
        }
      } catch (err) {
        console.error('Failed to boot conversations:', err);
        setConversationsLoading(false);
      }
    };
    bootConversations();

    return () => {
      window.speechSynthesis.cancel();
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(track => track.stop());
        micStreamRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (restartMicTimeoutRef.current) {
        clearTimeout(restartMicTimeoutRef.current);
        restartMicTimeoutRef.current = null;
      }
    };
  }, [currentProject?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Command cards expand by default only while running
  // (`expanded={commandExpanded[id] ?? status === 'running'}`). Manual toggles win.

  // Refresh conversation list + active status while any session is running
  useEffect(() => {
    const hasRunning = conversations.some(c => c.status === 'running' || c.status === 'waiting_user');
    const activeRunning =
      activeConversation?.status === 'running' || activeConversation?.status === 'waiting_user';
    if (!hasRunning && !activeRunning) return;
    const t = setInterval(() => {
      refreshConversations();
      if (activeConversationId && activeRunning) {
        selectConversation(activeConversationId, { keepLive: true });
      }
    }, 2500);
    return () => clearInterval(t);
  }, [
    conversations,
    refreshConversations,
    activeConversation?.status,
    activeConversationId,
    selectConversation,
  ]);

  const handleAction = async (action: any) => {
    console.log('🎬 Handling action:', action);

    if (action.type === 'open_ticket' || action.type === 'open_ticket_session' || action.type === 'open_agent_session') {
      // App listens and opens panel + switches conversation on the single ComplicChat instance
      window.dispatchEvent(
        new CustomEvent('complic-open-conversation', {
          detail: {
            conversationId: action.conversation_id,
            ticketKey: action.ticket_key,
          },
        })
      );
      if (onOpenTicket && action.type === 'open_ticket') {
        onOpenTicket(action.ticket_key, null);
      }
      if (action.type === 'open_agent_session' && action.pending_question) {
        addToast(`Agent: ${action.pending_question}`, 'info');
      }
    } else if (action.type === 'add_worklog') {
      // Backend already wrote to Jira; confirm in UI. Fallback POST if needed.
      if (action.logged) {
        addToast(`Logged ${action.hours}h on ${action.ticket_key}`, 'success');
      } else {
        try {
          await axios.post(`${API_URL}/api/tickets/${action.ticket_key}/worklog`, null, {
            params: {
              time_spent: String(action.hours),
              comment: action.comment || undefined,
              project_id: currentProject?.id,
            },
          });
          addToast(`Logged ${action.hours}h on ${action.ticket_key}`, 'success');
        } catch (err: any) {
          addToast(notifyJiraError(err), 'error');
        }
      }
    } else if (action.type === 'add_comment') {
      if (action.logged) {
        addToast(`Comment added on ${action.ticket_key}`, 'success');
      } else {
        try {
          await axios.post(`${API_URL}/api/tickets/${action.ticket_key}/comment`, null, {
            params: {
              body: action.comment,
              project_id: currentProject?.id,
            },
          });
          addToast(`Comment added on ${action.ticket_key}`, 'success');
        } catch (err: any) {
          addToast(notifyJiraError(err), 'error');
        }
      }
    } else if (action.type === 'ticket_transitioned') {
      addToast(
        `Ticket ${action.ticket_key} → ${action.transition_name || action.transition_id}`,
        'success'
      );
    } else if (action.type === 'request_git_push_confirmation') {
      const pushMsg: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content:
          `Chceš pushnout branch \`${action.branch}\` na origin?\n` +
          `Napiš „ano“ / „push“.`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, pushMsg]);
    } else if (action.type === 'git_pushed') {
      addToast(`Pushed ${action.branch}`, 'success');
    } else if (action.type === 'request_create_ticket_confirmation') {
      // Legacy: conversational confirm only (no dialog)
      const labels = Array.isArray(action.labels) ? action.labels.join(', ') : (action.labels || 'BE');
      const msg: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content:
          `Návrh tiketu:\n` +
          `• ${action.issue_type || 'Task'}: ${action.summary}\n` +
          `• Assignee: ${action.assignee || 'já'}\n` +
          `• Labels: ${labels}\n` +
          `Založit? Napiš „ano“ / „založ“.`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, msg]);
    } else if (action.type === 'ticket_created') {
      addToast(`Created ${action.ticket_key}`, 'success');
      if (onOpenTicket && action.ticket_key) {
        onOpenTicket(action.ticket_key, null);
      }
    } else if (action.type === 'ticket_updated') {
      addToast(`Updated ${action.ticket_key}`, 'success');
    } else if (action.type === 'agent_started' || action.type === 'ticket_work_started') {
      addToast(
        `${action.agent_id || 'developer'} ${action.mode || ''} on ${action.ticket_key}`,
        'success'
      );
      if (action.conversation_id) {
        await selectConversation(action.conversation_id);
      } else if (action.ticket_key) {
        await openTicketConversation(action.ticket_key);
      }
    } else if (action.type === 'job_started') {
      // Legacy — route into ticket session
      if (action.ticket_key) {
        await openTicketConversation(action.ticket_key);
      }
    } else if (action.type === 'show_analytics') {
      // Display analytics data inline in the chat
      const analyticsMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: '', // Empty content, will be replaced by AnalyticsCard
        timestamp: new Date(),
        analyticsData: { ...action.data, period: action.period }
      };
      setMessages(prev => [...prev, analyticsMessage]);
    } else if (action.type === 'show_prediction') {
      // Display prediction data inline in the chat
      const predictionMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: '', // Empty content, will be replaced by PredictionCard
        timestamp: new Date(),
        predictionData: action.data
      };
      setMessages(prev => [...prev, predictionMessage]);
    } else if (action.type === 'show_notifications') {
      // Display notifications inline in the chat
      const notificationsMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: '', // Empty content, will be replaced by NotificationsCard
        timestamp: new Date(),
        notificationsData: action.data
      };
      setMessages(prev => [...prev, notificationsMessage]);
    } else if (action.type === 'shutdown_request') {
      // Show confirmation dialog for shutdown
      const confirmed = window.confirm(
        'Opravdu chceš vypnout hlasového asistenta?'
      );

      if (confirmed) {
        // Stop voice assistant
        shouldContinueListeningRef.current = false;
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
        setIsListening(false);

        // Stop any ongoing speech
        shouldStopSpeakingRef.current = true;
        window.speechSynthesis.cancel();
        if (currentAudioRef.current) {
          currentAudioRef.current.pause();
          currentAudioRef.current = null;
        }
        setIsSpeakingWithRef(false);

        // Restart wake word after 1 second
        if (wakeWordRestartTimeoutRef.current) {
          clearTimeout(wakeWordRestartTimeoutRef.current);
        }
        wakeWordRestartTimeoutRef.current = setTimeout(() => {
          if (wakeWordEnabled && wakeWordControlRef.current) {
            wakeWordControlRef.current.start();
          }
        }, 1000);

        const confirmMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: '👋 Hlasový asistent vypnut. Nashledanou!',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, confirmMessage]);
      } else {
        const cancelMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: 'Dobře, zůstávám aktivní. 😊',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, cancelMessage]);
      }
    } else if (action.type === 'request_job_confirmation') {
      // Legacy: ask in chat only (no browser dialog). Real start = request_job_action confirmed=true.
      const msg: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content:
          `Spustit **${(action.action || 'plan').toUpperCase()}** na ${action.ticket_key}?\n` +
          `${action.reason ? `Důvod: ${action.reason}\n` : ''}` +
          `Napiš „ano“ / „spusť“.`,
        timestamp: new Date(),
        ticketKeys: action.ticket_key ? [action.ticket_key] : undefined,
      };
      setMessages(prev => [...prev, msg]);
    }
  };

  const fetchDefaultModel = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/model-defaults`);
      const complicAssistant = response.data.complic_assistant || 'default';
      const usesDefault = !complicAssistant || complicAssistant === 'default' || complicAssistant === 'auto';

      if (usesDefault) {
        setSelectedAssistant('default');
        setSelectedModel(response.data.default_model || '');
      } else {
        setSelectedAssistant(complicAssistant);
        setSelectedModel(response.data.complic || '');
      }
    } catch (err) {
      console.error('Failed to fetch default model:', err);
    }
  };

  const formatAssistantLabel = (assistant: string) => {
    const key = (assistant || 'default').toLowerCase();
    if (key === 'default' || key === 'auto') return 'Default';
    if (key === 'cursor') return 'Cursor';
    if (key === 'claude_cli') return 'Claude CLI';
    if (key === 'claude_code') return 'Claude API';
    if (key === 'aider') return 'Aider';
    return assistant;
  };

  const fetchAvailableVoices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/complic/tts/voices`);
      setAvailableCloudVoices(response.data.voices || []);
    } catch (err) {
      console.error('Failed to fetch voices:', err);
    }
  };

  const fetchInitialContext = async () => {
    if (!currentProject?.id) return null;

    try {
      const response = await axios.get(`${API_URL}/api/complic/context`, {
        params: { project_id: currentProject.id }
      });
      return response.data;
    } catch (err) {
      console.error('Failed to fetch initial context:', err);
      return null;
    }
  };

  const generateWelcomeMessage = (context: any): string => {
    if (!context) {
      return language === 'cs-CZ'
        ? 'Ahoj! Jsem Complic, tvůj AI asistent. Jak ti můžu pomoct?'
        : 'Hello! I\'m Complic, your AI coding assistant. How can I help you today?';
    }

    const parts: string[] = [];

    if (language === 'cs-CZ') {
      parts.push('👋 Ahoj!');

      if (context.project_name) {
        parts.push(`📁 Projekt: **${context.project_name}**`);
      }

      if (context.active_tickets_count > 0) {
        parts.push(`📋 Máš **${context.active_tickets_count}** aktivních tiketů`);
        if (context.sprint_tickets_count > 0) {
          parts.push(`   (${context.sprint_tickets_count} ve sprintu)`);
        }
      }

      if (context.today_worklog_hours > 0) {
        parts.push(`⏱️ Dnes zalogováno: **${context.today_worklog_hours}h**`);
      }

      if (context.running_jobs_count > 0) {
        parts.push(`🚀 Běží **${context.running_jobs_count}** job${context.running_jobs_count > 1 ? 's' : ''}`);
      }

      // Git status
      if (context.git_status) {
        const git = context.git_status;
        parts.push(`\n🔀 **Git status:**`);
        parts.push(`   • Branch: **${git.current_branch}**`);
        if (git.has_uncommitted) {
          parts.push(`   • Uncommitted: ${git.uncommitted_changes} souborů`);
        }
        if (git.last_commit_message) {
          parts.push(`   • Last commit: "${git.last_commit_message.substring(0, 40)}..."`);
        }
      }

      // Current context (what user is working on)
      if (context.current_context && context.current_context.current_ticket_key) {
        parts.push(`\n📍 **Pracuješ na:**`);
        parts.push(`   • Ticket: **${context.current_context.current_ticket_key}**`);
        if (context.current_context.current_branch) {
          parts.push(`   • Branch: ${context.current_context.current_branch}`);
        }
      }

      if (context.suggestions && context.suggestions.length > 0) {
        parts.push('\n💡 **Smart návrhy:**');
        context.suggestions.slice(0, 2).forEach((sug: any) => {
          parts.push(`   • ${sug.message}`);
        });
      }

      parts.push('\nJak ti můžu pomoct?');
    } else {
      parts.push('👋 Hello!');

      if (context.project_name) {
        parts.push(`📁 Project: **${context.project_name}**`);
      }

      if (context.active_tickets_count > 0) {
        parts.push(`📋 You have **${context.active_tickets_count}** active tickets`);
      }

      if (context.today_worklog_hours > 0) {
        parts.push(`⏱️ Today logged: **${context.today_worklog_hours}h**`);
      }

      if (context.running_jobs_count > 0) {
        parts.push(`🚀 Running **${context.running_jobs_count}** job(s)`);
      }

      parts.push('\nHow can I help you today?');
    }

    return parts.join('\n');
  };

  const initMicrophoneWithEchoCancellation = async () => {
    try {
      // Request microphone with echo cancellation
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          // Additional constraints for better echo cancellation
          sampleRate: 48000,
          channelCount: 1
        }
      });

      micStreamRef.current = stream;

      // Create audio context for volume analysis
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.8;
      microphone.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      console.log('✅ Microphone initialized with echo cancellation');
    } catch (err) {
      console.error('Failed to initialize microphone:', err);
    }
  };

  const initSpeechRecognition = () => {
    // Check if browser supports speech recognition
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false; // Keep false to trigger onend after each phrase
      recognition.interimResults = false;
      recognition.lang = language;

      // Try to enable echo cancellation (browser support varies)
      // Note: This is automatically handled by most browsers, but we can't directly control it via Web Speech API

      recognition.onresult = async (event: any) => {
        const transcript = event.results[0][0].transcript;
        const confidence = event.results[0][0].confidence;

        console.log('🎤 Voice input:', transcript);

        // Ignore very short inputs or low confidence (likely echo)
        if (transcript.trim().length < 5) {
          return;
        }

        if (confidence < 0.6) {
          return;
        }

        // ALWAYS stop any ongoing speech when user starts talking
        shouldStopSpeakingRef.current = true;
        window.speechSynthesis.cancel();
        if (currentAudioRef.current) {
          currentAudioRef.current.pause();
          currentAudioRef.current.currentTime = 0;
          currentAudioRef.current = null;
        }
        setIsSpeakingWithRef(false);

        // Small delay to ensure cleanup
        await new Promise(resolve => setTimeout(resolve, 100));

        // Set input to show what was recognized
        setInput(transcript);

        // Auto-send the message
        if (transcript.trim()) {
          // Create user message
          const userMessage: Message = {
            id: Date.now().toString(),
            role: 'user',
            content: transcript,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, userMessage]);
          setInput('');
          setLoading(true);
          setThinkingDetail('Thinking…');

          try {
            // Send to AI
            const response = await axios.post(`${API_URL}/api/complic/chat`, {
              message: transcript,
              model: selectedModel,
              language: language,
              history: messages.map(m => ({ role: m.role, content: m.content })),
              project_id: currentProject?.id,
              conversation_id: activeConversationIdRef.current,
              ticket_key: activeConversation?.ticket_key || undefined,
            });
            setModelUsed(response.data.model_used || '');
            setAssistantUsed(response.data.assistant_used || selectedAssistant);

            if (response.data.conversation_id && !activeConversationIdRef.current) {
              activeConversationIdRef.current = response.data.conversation_id;
              setActiveConversationId(response.data.conversation_id);
            }
            refreshConversations();

            const aiMessage: Message = {
              id: (Date.now() + 1).toString(),
              role: 'assistant',
              content: response.data.response,
              timestamp: new Date(),
              ticketKeys: extractTicketKeys(response.data.response),
            };
            setMessages(prev => [...prev, aiMessage]);

            // Handle action if present
            if (response.data.action) {
              handleAction(response.data.action);
            }

            // FIRST: Mark that AI is about to speak (BEFORE stopping mic)
            setIsSpeakingWithRef(true);

            // STOP listening completely while AI speaks
            if (recognitionRef.current && isListening) {
              recognitionRef.current.stop();
              setIsListening(false);
            }

            // Speak the response (recognition is OFF, isSpeaking is TRUE)
            await speakText(response.data.response);

            // Cancel any previous restart timeout
            if (restartMicTimeoutRef.current) {
              clearTimeout(restartMicTimeoutRef.current);
              restartMicTimeoutRef.current = null;
            }

            // After AI finishes, wait LONGER to ensure no echo
            if (shouldContinueListeningRef.current && recognitionRef.current) {
              restartMicTimeoutRef.current = setTimeout(() => {
                if (shouldContinueListeningRef.current) {
                  try {
                    recognitionRef.current.start();
                    setIsListening(true);
                    restartMicTimeoutRef.current = null;
                  } catch (e) {
                    // Failed to restart
                  }
                }
              }, 200); // Wait 800ms after AI finishes
            }
          } catch (error) {
            console.error('Error sending message:', error);
            const detail = (error as any)?.response?.data?.detail;
            const errorMessage: Message = {
              id: (Date.now() + 1).toString(),
              role: 'assistant',
              content: detail || 'Sorry, I encountered an error. Please try again.',
              timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
          } finally {
            setLoading(false);
            setThinkingDetail(null);
          }
        }
      };

      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);

        // Ignore aborted errors (happens when we stop it manually)
        if (event.error === 'aborted') {
          return;
        }

        // If no-speech timeout, stop voice assistant completely and restart wake word
        if (event.error === 'no-speech') {
          // Stop voice assistant completely (this will also restart wake word)
          shouldStopSpeakingRef.current = true;
          shouldContinueListeningRef.current = false;
          setIsListening(false);
          setIsSpeakingWithRef(false);

          if (recognitionRef.current) {
            try {
              recognitionRef.current.stop();
            } catch (e) {
              // Already stopped
            }
          }

          // Notify parent that voice timed out (so it can turn off isVoiceActive)
          if (onVoiceTimeout) {
            onVoiceTimeout();
          }

          // Restart wake word after a short delay
          if (wakeWordRestartTimeoutRef.current) {
            clearTimeout(wakeWordRestartTimeoutRef.current);
          }
          wakeWordRestartTimeoutRef.current = setTimeout(() => {
            if (wakeWordEnabled && wakeWordControlRef.current) {
              wakeWordControlRef.current.start();
            }
          }, 1000);
          return;
        }

        // Try to restart if should continue listening (for other errors)
        if (shouldContinueListeningRef.current) {
          setTimeout(() => {
            if (shouldContinueListeningRef.current && recognitionRef.current) {
              try {
                recognitionRef.current.start();
              } catch (e) {
                console.error('Failed to restart after error:', e);
              }
            }
          }, 500);
        } else {
          setIsListening(false);
        }
      };

      recognition.onend = () => {
        // NEVER auto-restart here - only restart from the timeout after AI finishes
        // This prevents restarting too early during AI speech
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    } else {
      console.warn('Speech recognition not supported in this browser');
    }
  };

  const toggleListening = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser');
      return;
    }

    if (isListening) {
      // Stop continuous listening
      shouldContinueListeningRef.current = false;
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      // Start continuous listening
      shouldContinueListeningRef.current = true;
      recognitionRef.current.lang = language;
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const speakText = async (text: string) => {
    // Cancel any ongoing speech first
    shouldStopSpeakingRef.current = true;
    window.speechSynthesis.cancel();
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }

    // Small delay to ensure previous audio is stopped
    await new Promise(resolve => setTimeout(resolve, 100));

    // Now start fresh
    shouldStopSpeakingRef.current = false;
    aiStartedSpeakingAtRef.current = Date.now();
    // isSpeaking should already be true (set before calling this), but ensure it
    if (!isSpeakingRef.current) {
      setIsSpeakingWithRef(true);
    }

    // Stop wake word during TTS to prevent self-triggering
    if (wakeWordControlRef.current && wakeWordControlRef.current.isListening) {
      wakeWordControlRef.current.stop();
    }

    // Use Google Translate TTS for Czech (best quality, free)
    if (language === 'cs-CZ') {
      try {
        setCurrentVoice('🌐 Google Translate - Czech Neural Voice');
        await speakWithGoogleTTS(text);
        return;
      } catch (e) {
        setCurrentVoice('⚠️ Google TTS failed, using fallback');
      }
    }

    // Fallback to browser TTS
    speakWithBrowserTTS(text);
  };

  const speakWithGoogleTTS = async (text: string) => {
    try {
      // Split text into chunks (5000 chars max for Cloud TTS)
      const chunks = text.match(/.{1,5000}/g) || [text];

      for (const chunk of chunks) {
        // Check if stopped
        if (shouldStopSpeakingRef.current) {
          break;
        }

        // Use our backend with selected voice (read from localStorage for freshest value)
        const currentVoice = localStorage.getItem('complic_selected_voice') || selectedCloudVoice;
        const response = await axios.post(`${API_URL}/api/complic/tts`, {
          text: chunk,
          lang: language,
          voice: currentVoice,
          speed: 1.0
        }, {
          responseType: 'blob'
        });

        // Create audio from blob
        const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        audio.volume = 1.0;
        audio.playbackRate = 1.0;

        // Store reference to current audio
        currentAudioRef.current = audio;

        // Play and wait for completion
        await new Promise((resolve, reject) => {
          // Check periodically if we should stop
          const checkInterval = setInterval(() => {
            if (shouldStopSpeakingRef.current) {
              clearInterval(checkInterval);
              audio.pause();
              audio.currentTime = 0;
              URL.revokeObjectURL(audioUrl);
              currentAudioRef.current = null;
              resolve(null);
            }
          }, 100);

          audio.onended = () => {
            clearInterval(checkInterval);
            console.log('✅ Audio playback completed');
            URL.revokeObjectURL(audioUrl);
            currentAudioRef.current = null;
            resolve(null);
          };
          audio.onerror = (e) => {
            clearInterval(checkInterval);
            console.error('❌ Audio playback error:', e);
            URL.revokeObjectURL(audioUrl);
            currentAudioRef.current = null;
            reject(e);
          };

          audio.play()
            .then(() => console.log('▶️ Playing audio'))
            .catch(reject);
        });
      }

      setIsSpeakingWithRef(false);
      currentAudioRef.current = null;
      // Wake word will restart via stopVoice timeout (don't restart here)
    } catch (e) {
      setIsSpeakingWithRef(false);
      // Wake word will restart via stopVoice timeout (don't restart here)
      currentAudioRef.current = null;
      throw e;
    }
  };

  const speakWithBrowserTTS = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language;
    utterance.rate = 0.95;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    let selectedVoice: SpeechSynthesisVoice | undefined;

    // Find the best voice from loaded voices
    if (language === 'cs-CZ') {
      selectedVoice =
        voices.find(voice => voice.name.toLowerCase().includes('zuzana')) ||
        voices.find(voice => voice.name.toLowerCase().includes('google') && voice.lang === 'cs-CZ') ||
        voices.find(voice => voice.lang === 'cs-CZ') ||
        voices.find(voice => voice.lang.startsWith('cs')) ||
        voices.find(voice => voice.name.toLowerCase().includes('czech'));

      if (selectedVoice) {
        console.log('✅ Using local Czech voice:', selectedVoice.name);
        utterance.voice = selectedVoice;
        setCurrentVoice(`💻 ${selectedVoice.name} (${selectedVoice.localService ? 'Local' : 'Online'})`);
      } else {
        setCurrentVoice('❌ No Czech voice available');
      }
    } else {
      selectedVoice =
        voices.find(voice => voice.name.toLowerCase().includes('google') && voice.lang === 'en-US') ||
        voices.find(voice => voice.lang === 'en-US') ||
        voices.find(voice => voice.lang.startsWith('en'));

      if (selectedVoice) {
        utterance.voice = selectedVoice;
        setCurrentVoice(`💻 ${selectedVoice.name} (${selectedVoice.localService ? 'Local' : 'Online'})`);
      } else {
        setCurrentVoice('💻 Browser Default Voice');
      }
    }

    utterance.onstart = () => setIsSpeakingWithRef(true);
    utterance.onend = () => setIsSpeakingWithRef(false);
    utterance.onerror = () => {
      setIsSpeakingWithRef(false);
    };

    window.speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    // Set flag to stop any ongoing speech
    shouldStopSpeakingRef.current = true;

    // Stop browser TTS
    window.speechSynthesis.cancel();

    // Stop current audio if playing
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }

    setIsSpeakingWithRef(false);
    console.log('⏹️ Stopped speaking');
  };

  const clearChatHistory = () => {
    if (window.confirm('Vytvořit nový chat? Aktuální konverzace zůstane v seznamu.')) {
      createGeneralConversation();
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const text = input.trim();
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLastSentText(text);
    setLoading(true);
    setThinkingDetail(
      activeConversation?.kind === 'ticket' &&
        ['running', 'waiting_user', 'pending'].includes(activeConversation.status || '')
        ? 'Odesílám agentovi…'
        : 'Thinking…'
    );

    const abort = new AbortController();
    chatAbortRef.current = abort;

    try {
      // If ticket session has an active agent, talk to the agent directly
      const agentStatuses = new Set(['running', 'waiting_user', 'pending', 'queued']);
      const isTicket = activeConversation?.kind === 'ticket';
      const agentBusy = agentStatuses.has(activeConversation?.status || '');

      if (activeConversationIdRef.current && isTicket && agentBusy) {
        try {
          const agentRes = await axios.post(
            `${API_URL}/api/complic/conversations/${activeConversationIdRef.current}/agent-message`,
            { message: text },
            { signal: abort.signal }
          );
          // Soft refresh only — do NOT rehydrate finished command history
          await selectConversation(activeConversationIdRef.current, { keepLive: true });
          refreshConversations();
          if (agentRes.data.reply) {
            addToast(agentRes.data.reply, 'success');
          }
          return;
        } catch (agentErr: any) {
          if (axios.isCancel(agentErr) || agentErr?.code === 'ERR_CANCELED' || abort.signal.aborted) {
            return;
          }
          if (agentErr?.response?.status !== 409) {
            throw agentErr;
          }
          // fall through — no active agent
        }
      }

      // Ticket session idle: "dokonci to" / continue → start Developer with the note
      // (avoids hanging on Complic Cursor chat with no live command UI)
      const continueHint =
        /dokonč|dokonc|pokrač|pokrac|continue|finish|resume|opravit|fix it|dál|další|go\b/i.test(
          text
        );
      if (
        activeConversationIdRef.current &&
        isTicket &&
        !agentBusy &&
        currentProject?.id &&
        continueHint
      ) {
        setThinkingDetail('Spouštím Developera…');
        // Persist the user note (optimistic bubble is already in UI)
        try {
          await axios.post(
            `${API_URL}/api/complic/conversations/${activeConversationIdRef.current}/messages`,
            { role: 'user', content: text },
            { signal: abort.signal }
          );
        } catch {
          /* non-fatal */
        }
        const workRes = await axios.post(
          `${API_URL}/api/complic/conversations/${activeConversationIdRef.current}/work`,
          {
            mode: 'full',
            project_id: currentProject.id,
            reason: text,
          },
          { signal: abort.signal }
        );
        setActiveConversation((prev) =>
          prev
            ? {
                ...prev,
                status: workRes.data.queued ? 'queued' : 'running',
                status_detail: workRes.data.queued
                  ? `Ve frontě (pozice ${workRes.data.position})`
                  : 'Developer…',
              }
            : prev
        );
        addToast(
          workRes.data.queued
            ? `Developer ve frontě · ${(workRes.data.run_id || '').slice(0, 8)}`
            : `Developer spuštěn · ${(workRes.data.run_id || '').slice(0, 8)}`,
          'success'
        );
        await selectConversation(activeConversationIdRef.current, { keepLive: true });
        refreshConversations();
        return;
      }

      const response = await axios.post(
        `${API_URL}/api/complic/chat`,
        {
          message: text,
          model: selectedModel,
          language: language,
          history: messages.map(m => ({ role: m.role, content: m.content })),
          project_id: currentProject?.id,
          conversation_id: activeConversationIdRef.current,
          ticket_key: activeConversation?.ticket_key || undefined,
        },
        { signal: abort.signal }
      );
      setModelUsed(response.data.model_used || '');
      setAssistantUsed(response.data.assistant_used || selectedAssistant);

      if (response.data.conversation_id && !activeConversationIdRef.current) {
        activeConversationIdRef.current = response.data.conversation_id;
        setActiveConversationId(response.data.conversation_id);
      }
      refreshConversations();

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date(),
        ticketKeys: extractTicketKeys(response.data.response)
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Handle action if present
      if (response.data.action) {
        handleAction(response.data.action);
      }

      // Typed chat stays silent; voice mode speaks via recognition flow
    } catch (err: any) {
      if (axios.isCancel(err) || err?.code === 'ERR_CANCELED' || abort.signal.aborted) {
        setMessages(prev => [
          ...prev,
          {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: '⏹ Zastaveno. Můžeš poslat znovu.',
            timestamp: new Date(),
          },
        ]);
        return;
      }
      console.error('Chat error:', err);
      const detail = err?.response?.data?.detail;
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: detail || 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      if (chatAbortRef.current === abort) {
        chatAbortRef.current = null;
      }
      setLoading(false);
      setThinkingDetail(null);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Extract real Jira keys only — ignore fixture ids like UNIT-001 / COM-021
  const extractTicketKeys = (content: string): string[] => {
    const FALSE_PREFIXES = new Set([
      'UNIT', 'COM', 'FAC', 'PER', 'HTTP', 'HTTPS', 'UTF', 'ISO', 'API', 'CSS', 'HTML',
      'JSON', 'XML', 'PDF', 'PNG', 'JPG', 'SVG', 'SQL', 'PHP', 'RFC', 'UTC', 'GMT',
      'UUID', 'NULL', 'TRUE', 'FALSE', 'ERROR', 'WARN', 'INFO', 'DEBUG', 'TEST',
      'DEMO', 'MOCK', 'TEMP', 'TMP', 'V1', 'V2', 'V3', 'ID', 'OK', 'BE', 'FE', 'QA',
    ]);
    const projectKey = (currentProject as any)?.jira_project_key?.toUpperCase?.();
    const ticketPattern = /\b([A-Z][A-Z0-9]{1,9}-\d+)\b/g;
    const matches = content.match(ticketPattern) || [];
    return [...new Set(matches)].filter((key) => {
      const prefix = key.split('-')[0];
      if (FALSE_PREFIXES.has(prefix)) return false;
      if (prefix.length < 2 || prefix.length > 6) return false;
      if (projectKey) return prefix === projectKey;
      return true;
    });
  };

  const handleTicketClick = (ticketKey: string) => {
    openTicketConversation(ticketKey);
    if (onOpenTicket) {
      onOpenTicket(ticketKey, null);
    }
  };

  const handleQuickAction = (_ticketKey: string, _action: string, _params?: any) => {
    console.log('Quick action functionality removed');
  };



  return (
    <>
      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <div className="h-full w-full flex justify-end">
        {/* Main Container - embedded side panel */}
        <div className={`h-full w-full bg-white dark:bg-gray-800 rounded-lg shadow-2xl flex transition-all duration-300 ${
          showSplitView ? 'max-w-none' : 'max-w-none'
        } animate-slide-in-right`}>

          {showConversationSidebar && (
            <ComplicConversationSidebar
              conversations={conversations}
              activeId={activeConversationId}
              loading={conversationsLoading}
              onSelect={selectConversation}
              onNewGeneral={createGeneralConversation}
              onRefresh={refreshConversations}
            />
          )}

          {/* Chat Section */}
          <div className={`flex flex-col transition-all duration-300 min-w-0 ${
            showSplitView ? 'w-3/5 border-r border-gray-200 dark:border-gray-700' : 'flex-1'
          }`}>
            {/* Header */}
            <div className="flex flex-col border-b border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/80 shrink-0">
              <div className="flex items-center gap-2 p-3 min-w-0">
                <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
                  <button
                    onClick={() => setShowConversationSidebar(v => !v)}
                    className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 shrink-0"
                    title="Konverzace"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h10" />
                    </svg>
                  </button>
                  <div className="relative shrink-0">
                    <div className={`w-9 h-9 rounded-xl bg-gradient-to-br from-complic-500 to-sky-500 flex items-center justify-center shadow-soft transition-all duration-300 ${wakeWordDetected ? 'scale-110 ring-2 ring-emerald-400/60' : ''}`}>
                      <span className="text-white font-bold text-sm">C</span>
                    </div>
                    {wakeWordDetected && (
                      <div className="absolute -inset-1.5 rounded-xl border-2 border-emerald-400 animate-ping opacity-60"></div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <h2
                      className="text-sm font-bold text-slate-800 dark:text-white normal-case truncate"
                      title={
                        activeConversation?.kind === 'ticket' && activeConversation.ticket_key
                          ? activeConversation.ticket_key
                          : (activeConversation?.title || 'Complic')
                      }
                    >
                      {activeConversation?.kind === 'ticket' && activeConversation.ticket_key
                        ? activeConversation.ticket_key
                        : (activeConversation?.title || 'Complic')}
                    </h2>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                      {sessionLiveLog
                        || activeConversation?.status_detail
                        || (activeConversation?.kind === 'ticket' ? 'Ticket session' : 'Ctrl+K · Esc')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  {activeConversation?.kind === 'ticket' && (
                    <>
                      <button
                        disabled={startingWork || isAgentBusy || stopping}
                        onClick={() => startTicketWork('plan')}
                        className="px-2 py-1 text-[11px] rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white"
                        title="Developer · plan"
                      >
                        Plan
                      </button>
                      <button
                        disabled={startingWork || isAgentBusy || stopping}
                        onClick={() => startTicketWork('full')}
                        className="px-2 py-1 text-[11px] rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white"
                        title="Developer · full (plan→PR)"
                      >
                        Dev
                      </button>
                      {isBusy && (
                        <button
                          disabled={stopping}
                          onClick={handleStop}
                          className="px-2 py-1 text-[11px] rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white"
                          title="Stop"
                        >
                          {stopping ? '…' : 'Stop'}
                        </button>
                      )}
                    </>
                  )}

                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value as 'en-US' | 'cs-CZ')}
                    className="hidden sm:block max-w-[5.5rem] px-1.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-white text-[11px] border border-slate-200 dark:border-slate-600"
                    title="Language"
                  >
                    <option value="cs-CZ">CS</option>
                    <option value="en-US">EN</option>
                  </select>

                  <button
                    onClick={() => setShowSplitView(prev => !prev)}
                    className={`p-1.5 rounded-lg transition ${
                      showSplitView
                        ? 'bg-complic-600 text-white'
                        : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300'
                    }`}
                    title="Toggle split view"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4H5a2 2 0 00-2 2v14a2 2 0 002 2h4m0-18v18m0-18a2 2 0 012-2h8a2 2 0 012 2v14a2 2 0 01-2 2h-8a2 2 0 01-2-2" />
                    </svg>
                  </button>

                  <button
                    onClick={onClose}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 dark:hover:text-white text-xl leading-none"
                    title="Close"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>

            {/* Info bar under header */}
            <div className="px-3 py-1.5 bg-slate-50/90 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700 shrink-0">
              <div className="flex items-center justify-between gap-2 min-w-0">
                <p className="text-[11px] text-slate-600 dark:text-slate-400 flex items-center gap-2 flex-wrap min-w-0">
                  <span className="font-medium truncate">{formatAssistantLabel(assistantUsed || selectedAssistant)}</span>
                  <span className="text-slate-300">·</span>
                  <span className="font-medium truncate">{(modelUsed || selectedModel) === 'auto' || (modelUsed || selectedModel) === 'default' ? 'Default' : (modelUsed || selectedModel)}</span>
                  {isSpeaking && (
                    <>
                      <span className="text-slate-300">·</span>
                      <span className="text-complic-600 dark:text-complic-300 animate-pulse font-medium">Speaking…</span>
                    </>
                  )}
                </p>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {language === 'cs-CZ' && availableCloudVoices.length > 0 && (
                    <select
                      value={selectedCloudVoice}
                      onChange={(e) => {
                        const newVoice = e.target.value;
                        localStorage.setItem('complic_selected_voice', newVoice);
                        setSelectedCloudVoice(newVoice);
                        if (currentAudioRef.current) {
                          currentAudioRef.current.pause();
                          currentAudioRef.current = null;
                        }
                        window.speechSynthesis.cancel();
                      }}
                      className="max-w-[7rem] px-1.5 py-0.5 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-[10px] border border-slate-200 dark:border-slate-600"
                      title="Voice"
                    >
                      {availableCloudVoices.map((voice) => (
                        <option key={voice.id} value={voice.id}>
                          {voice.name}
                        </option>
                      ))}
                    </select>
                  )}
                  <button
                    onClick={clearChatHistory}
                    className="p-1 rounded-lg hover:bg-complic-50 dark:hover:bg-complic-900/30 text-complic-700 dark:text-complic-300 transition"
                    title="New chat (Ctrl+L)"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>

                  {/* Settings button */}
                  <button
                    onClick={() => onOpenSettings?.()}
                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition"
                    title="Settings"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {activeConversation?.kind === 'ticket' && (activeConversation.progress || activeConversation.status === 'running' || activeConversation.status === 'waiting_user') && (
              <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 space-y-1.5">
                <div className="flex flex-wrap gap-2 items-center">
                  {(['plan', 'implement', 'validate', 'push', 'pr', 'review'] as const).map((phase) => {
                    const state = (activeConversation.progress || {})[phase] || 'pending';
                    const color =
                      state === 'done'
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                        : state === 'running'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 animate-pulse'
                          : state === 'failed'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                            : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400';
                    return (
                      <span
                        key={phase}
                        className={`text-[11px] font-sans px-2 py-0.5 rounded ${color}`}
                      >
                        {phase}
                        {state === 'done' ? ' ✓' : state === 'running' ? ' …' : state === 'failed' ? ' ✕' : ''}
                      </span>
                    );
                  })}
                  {activeConversation.branch_name && (
                    <span className="text-[11px] font-mono text-gray-500 truncate ml-auto">
                      {activeConversation.branch_name}
                    </span>
                  )}
                  {isAgentBusy && (
                    <button
                      type="button"
                      disabled={stopping}
                      onClick={() => void handleStop()}
                      className="text-[11px] px-2 py-0.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 ml-auto"
                      title="Zastavit agenta"
                    >
                      {stopping ? '…' : 'Stop ×'}
                    </button>
                  )}
                </div>
                {(sessionLiveLog || activeConversation.status_detail) && (
                  <p className="text-[11px] font-mono text-blue-700 dark:text-blue-300 truncate">
                    {sessionLiveLog || activeConversation.status_detail}
                  </p>
                )}
              </div>
            )}

            {/* Messages (+ inline agent commands in the same scroll) */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 scroll-smooth">
              {messages.map((message, index) => (
                message.kind === 'command' && message.command ? (
                  <div
                    key={message.id}
                    className="flex justify-start animate-slide-in-up"
                    style={{ animationDelay: `${Math.min(index, 8) * 30}ms` }}
                  >
                    <div className="w-full max-w-[92%]">
                      <AgentCommandCard
                        command={message.command}
                        expanded={
                          commandExpanded[message.command.id] ??
                          message.command.status === 'running'
                        }
                        onToggle={() => {
                          const id = message.command!.id;
                          const currently =
                            commandExpanded[id] ?? message.command!.status === 'running';
                          // Manual toggle allowed, but keep at most one open unless user opens a done card
                          setCommandExpanded((prev) => {
                            if (currently) {
                              return { ...prev, [id]: false };
                            }
                            const next: Record<string, boolean> = {};
                            Object.keys(prev).forEach((k) => {
                              next[k] = false;
                            });
                            next[id] = true;
                            return next;
                          });
                        }}
                        onStop={
                          message.command.status === 'running' && isAgentBusy
                            ? () => {
                                void handleStop();
                              }
                            : undefined
                        }
                      />
                    </div>
                  </div>
                ) : message.kind === 'activity' ? (
                  <div
                    key={message.id}
                    className="flex justify-start animate-slide-in-up"
                    style={{ animationDelay: `${Math.min(index, 8) * 30}ms` }}
                  >
                    <div
                      className={`max-w-[92%] rounded-lg px-3 py-2 text-[12px] font-sans leading-snug border ${
                        message.toolName === 'agent_milestone'
                          ? 'bg-indigo-50/90 dark:bg-indigo-950/40 border-indigo-200 dark:border-indigo-800 text-indigo-900 dark:text-indigo-100'
                          : message.toolName === 'agent_chat_ack' || message.toolName === 'agent_user_note'
                            ? 'bg-amber-50/90 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100'
                            : 'bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    </div>
                  </div>
                ) : (
                <div
                  key={message.id}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} animate-slide-in-up`}
                  style={{ animationDelay: `${Math.min(index, 8) * 30}ms` }}
                >
                  <div className="flex items-start gap-2 max-w-[85%]">
                    <div
                      className={`
                        rounded-lg px-4 py-3 font-sans flex-1 shadow-sm transition-all duration-200 hover:shadow-md
                        ${message.role === 'user'
                          ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white'
                          : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-white border border-gray-200 dark:border-gray-600'
                        }
                      `}
                    >
                      {/* Analytics Card */}
                      {message.analyticsData ? (
                        <AnalyticsCard data={message.analyticsData} period={message.analyticsData.period || 'week'} />
                      ) : message.predictionData ? (
                        <PredictionCard data={message.predictionData} />
                      ) : message.notificationsData ? (
                        <NotificationsCard
                          notifications={message.notificationsData.notifications}
                          onTicketClick={(ticketKey) => handleTicketClick(ticketKey)}
                        />
                      ) : (
                        <>
                          <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
                          <p className="text-xs opacity-60 mt-2 flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {message.timestamp.toLocaleTimeString()}
                          </p>
                        </>
                      )}

                      {/* Rich Ticket Cards — only real Jira keys */}
                      {message.role === 'assistant' && message.ticketKeys && message.ticketKeys.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600 space-y-2">
                          {message.ticketKeys.map(ticketKey => (
                            <div
                              key={ticketKey}
                              className="p-3 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 hover:shadow-md transition-all duration-200 cursor-pointer"
                              onClick={() => handleTicketClick(ticketKey)}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-bold text-blue-700 dark:text-blue-300 text-sm flex items-center gap-2">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  {ticketKey}
                                </h4>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTicketClick(ticketKey);
                                  }}
                                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 font-medium"
                                >
                                  View Details →
                                </button>
                              </div>
                              <div className="flex flex-wrap gap-1 mt-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuickAction(ticketKey, 'log_time', { time_spent: '1h' });
                                  }}
                                  className="px-2 py-1 text-xs rounded-md bg-blue-500 hover:bg-blue-600 text-white transition-all duration-200 font-medium shadow-sm hover:shadow"
                                  title="Log 1 hour"
                                >
                                  📝 Log 1h
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuickAction(ticketKey, 'move_to_review');
                                  }}
                                  className="px-2 py-1 text-xs rounded-md bg-green-500 hover:bg-green-600 text-white transition-all duration-200 font-medium shadow-sm hover:shadow"
                                  title="Move to Review"
                                >
                                  ✅ Review
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuickAction(ticketKey, 'start_plan_and_implement');
                                  }}
                                  className="px-2 py-1 text-xs rounded-md bg-purple-500 hover:bg-purple-600 text-white transition-all duration-200 font-medium shadow-sm hover:shadow"
                                  title="Plan + Implement"
                                >
                                  🚀 Plan+Impl
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Speaker button for assistant messages */}
                    {message.role === 'assistant' && (
                      <button
                        onClick={() => speakText(message.content)}
                        className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-600 dark:hover:bg-gray-500 transition-all duration-200 shadow-sm hover:shadow"
                        title="Speak this message"
                      >
                        <svg className="w-4 h-4 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.414C3.795 13.623 3.795 10.377 5.586 8.586l4-4a1 1 0 011.414 0l4 4c1.791 1.791 1.791 5.037 0 6.828l-4 4a1 1 0 01-1.414 0l-4-4z" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                )
              ))}

              {/* Loading Skeleton */}
              {loading && (
                <div className="flex justify-start animate-slide-in-up">
                  <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-4 py-3 shadow-sm max-w-[90%]">
                    <div className="flex gap-2 items-start">
                      <div className="flex gap-1 mt-1.5 shrink-0">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                      <div className="flex flex-col gap-2 min-w-0">
                        <span className="text-xs text-gray-600 dark:text-gray-300 font-medium leading-relaxed">
                          {thinkingDetail || sessionLiveLog || activeConversation?.status_detail || 'Thinking...'}
                        </span>
                        <button
                          type="button"
                          onClick={handleStop}
                          disabled={stopping}
                          className="self-start text-xs px-2 py-1 rounded bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-900/40 dark:hover:bg-red-900/60 dark:text-red-200 disabled:opacity-50"
                        >
                          {stopping ? 'Zastavuji…' : 'Stop'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              {isListening && (
                <div className="mb-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border border-blue-300 dark:border-blue-700 rounded-lg shadow-sm animate-pulse-slow">
                  <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400 text-sm font-sans font-medium">
                    <span className="text-lg">🎤</span>
                    <span>Listening... Speak now</span>
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    // Ctrl+Enter to send
                    if (e.ctrlKey && e.key === 'Enter') {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder={
                    isAgentBusy
                      ? 'Napiš agentovi… (Enter odešle do session)'
                      : 'Type your message... (Ctrl+Enter to send, Shift+Enter for new line)'
                  }
                  className="flex-1 px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white font-sans text-sm resize-none focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900/50 transition-all duration-200"
                  rows={2}
                  disabled={loading || isListening}
                />
                <div className="flex flex-col gap-2">
                  <button
                    onClick={toggleListening}
                    disabled={loading}
                    className={`p-3 rounded-lg transition-all duration-200 shadow-sm hover:shadow ${
                      isListening
                        ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                        : 'bg-gray-500 hover:bg-gray-600'
                    } text-white disabled:bg-gray-400`}
                    title={isListening ? 'Stop listening (Ctrl+M)' : 'Voice input (Ctrl+M)'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  </button>
                  <div className="flex gap-1">
                    {isAgentBusy && (
                      <button
                        onClick={handleStop}
                        disabled={stopping}
                        className="px-3 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 disabled:from-gray-400 disabled:to-gray-400 text-white rounded-lg font-sans font-semibold transition-all duration-200 shadow-sm hover:shadow disabled:opacity-50"
                        title="Zastavit agenta"
                      >
                        {stopping ? '…' : 'Stop'}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (!input.trim() && lastSentText && !isAgentBusy) {
                          setInput(lastSentText);
                          return;
                        }
                        handleSend();
                      }}
                      disabled={
                        loading ||
                        isListening ||
                        (!input.trim() && !(lastSentText && !isAgentBusy))
                      }
                      className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-400 text-white rounded-lg font-sans font-semibold transition-all duration-200 shadow-sm hover:shadow disabled:opacity-50"
                      title={
                        isAgentBusy
                          ? 'Odeslat agentovi (Enter)'
                          : input.trim()
                            ? 'Send message (Ctrl+Enter)'
                            : lastSentText
                              ? 'Vložit poslední zprávu'
                              : 'Send message (Ctrl+Enter)'
                      }
                    >
                      {isAgentBusy ? 'Send' : input.trim() ? 'Send' : lastSentText ? 'Retry' : 'Send'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Split View - Ticket Detail Panel */}
          {showSplitView && (
            <div className="w-2/5 bg-gray-50 dark:bg-gray-900 overflow-y-auto animate-slide-in-right">
              {selectedTicketForDetail ? (
                <TicketDetailPanel
                  ticketKey={selectedTicketForDetail.key}
                  ticketData={selectedTicketForDetail}
                  onClose={() => {
                    setSelectedTicketForDetail(null);
                    setShowSplitView(false);
                  }}
                  showTransitions={false}
                />
              ) : (
                <div className="h-full flex items-center justify-center p-8">
                  <div className="text-center space-y-4">
                    <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 flex items-center justify-center">
                      <svg className="w-10 h-10 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-2">No Ticket Selected</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Click on a ticket card to view details here
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
});

ComplicChat.displayName = 'ComplicChat';

export default ComplicChat;
